// src/v3/ui/backtester/results-viewer.tsx

import { isRecord } from "@signals/shared";
import type { ColumnDef } from "@tanstack/react-table";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ClientTable,
  EmptyState,
  SmartTable,
  type SmartTableColumnMeta,
} from "../_common";
import type { BacktestResult } from "./backtester.hooks";
import { DrawdownChart, EquityCurve, Heatmap, MonteCarloChart } from "./charts";

type TradeRow = Record<string, unknown>;

const optimizationRunSchema = z.object({
  params: z.record(z.string(), z.unknown()),
  summary: z.record(z.string(), z.unknown()),
});
type OptimizationRun = z.infer<typeof optimizationRunSchema>;

function readNumber(record: Record<string, unknown>, key: string): number {
  const value = record[key];
  return typeof value === "number" ? value : 0;
}


const optimizationRunColumns: ColumnDef<OptimizationRun>[] = [
  {
    accessorFn: (row) => JSON.stringify(row.params),
    header: "Params",
    meta: { format: "id" } satisfies SmartTableColumnMeta,
  },
  {
    id: "winRate",
    header: "Win Rate",
    accessorFn: (row) => readNumber(row.summary, "winRate"),
    meta: {
      format: "percent",
      formatOptions: { decimals: 1 },
    } satisfies SmartTableColumnMeta,
  },
  {
    id: "totalReturn",
    header: "Total Return",
    accessorFn: (row) =>
      readNumber(row.summary, "totalReturnPct"),
    meta: {
      format: "number",
      formatOptions: { decimals: 2, suffix: "%" },
    } satisfies SmartTableColumnMeta,
  },
  {
    id: "sharpe",
    header: "Sharpe",
    accessorFn: (row) => readNumber(row.summary, "sharpe"),
    meta: {
      format: "number",
      formatOptions: { decimals: 2 },
    } satisfies SmartTableColumnMeta,
  },
];

const optimizationResultSchema = z.object({
  strategy: z.string().optional(),
  stabilityScore: z.number().optional(),
  stabilityNotes: z.array(z.string()).optional(),
  bestParams: z.record(z.string(), z.unknown()).optional(),
  heatmap: z
    .array(
      z.object({
        paramX: z.string(),
        paramY: z.string(),
        valueX: z.unknown(),
        valueY: z.unknown(),
        winRate: z.number(),
        avgPnl: z.number(),
        totalReturn: z.number(),
      }),
    )
    .optional(),
  results: z.array(optimizationRunSchema).optional(),
});

export function ResultsViewer({ result }: { result: BacktestResult }) {
  const s = result.summary;
  const portfolio = result.portfolio;
  const monteCarlo = result.monteCarlo;
  const heatmap = result.heatmap;
  const breakdowns = result.breakdowns;
  const benchmark = result.benchmark;

  const tradeColumns: ColumnDef<TradeRow>[] = [
    {
      id: "index",
      header: "#",
      cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "symbol",
      header: "Symbol",
      meta: { format: "text" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "direction",
      header: "Direction",
      meta: { format: "text" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "entryPrice",
      header: "Entry",
      meta: { format: "currency" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "exitPrice",
      header: "Exit",
      meta: { format: "currency" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "pnl",
      header: "PnL",
      meta: { format: "currency" } satisfies SmartTableColumnMeta,
    },
    {
      accessorKey: "exitReason",
      header: "Exit Reason",
      meta: { format: "text" } satisfies SmartTableColumnMeta,
    },
    {
      id: "aiVerdict",
      header: "AI Verdict",
      accessorFn: (row) => {
        const v = row["aiVerdict"];
        if (!v || v === null) return undefined;
        if (
          typeof v === "object" &&
          isRecord(v) && "decision" in v
        ) {
          return v["decision"];
        }
        return v;
      },
      meta: { format: "text" } satisfies SmartTableColumnMeta,
    },
  ];

  const breakdownEntries = Object.entries(breakdowns ?? {});

  return (
    <Card>
      <CardHeader>
        <CardTitle>Results — {result.strategy}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {result.stalenessWarning && (
          <div className="rounded-md border border-yellow-400/50 bg-yellow-400/10 p-3 text-sm text-yellow-600">
            {result.stalenessWarning}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Net Profit" value={`$${s.netProfit.toFixed(2)}`} />
          <Stat label="Total Trades" value={String(s.totalTrades)} />
          <Stat label="Win Rate" value={`${(s.winRate * 100).toFixed(1)}%`} />
          <Stat label="Sharpe" value={s.sharpe.toFixed(2)} />
          <Stat label="Max DD" value={`${(s.maxDrawdown * 100).toFixed(1)}%`} />
          <Stat label="Profit Factor" value={s.profitFactor.toFixed(2)} />
        </div>

        {benchmark && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Benchmark vs Buy &amp; Hold</h4>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat
                label="Buy & Hold Return"
                value={`${benchmark.buyHoldReturnPct.toFixed(2)}%`}
              />
              <Stat
                label="Buy & Hold Max DD"
                value={`${benchmark.buyHoldMaxDrawdownPct.toFixed(2)}%`}
              />
              <Stat
                label="Buy & Hold Sharpe"
                value={benchmark.buyHoldSharpe.toFixed(2)}
              />
              <Stat
                label="vs Benchmark"
                value={`${benchmark.vsBenchmark >= 0 ? "+" : ""}${benchmark.vsBenchmark.toFixed(2)}%`}
              />
            </div>
          </div>
        )}

        {breakdownEntries.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Breakdowns</h4>
            <div className="grid gap-2 sm:grid-cols-2">
              {breakdownEntries.map(([key, value]) => (
                <div key={key} className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">{key}</div>
                  <pre className="mt-1 text-xs overflow-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {portfolio?.equityCurve && portfolio.equityCurve.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Equity Curve</h4>
            <EquityCurve data={portfolio.equityCurve} />
          </div>
        )}

        {portfolio?.drawdown && portfolio.drawdown.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Drawdown</h4>
            <DrawdownChart data={portfolio.drawdown} />
          </div>
        )}

        {monteCarlo && monteCarlo.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Monte Carlo Distribution</h4>
            <MonteCarloChart data={monteCarlo} />
          </div>
        )}

        {heatmap && heatmap.cells.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Optimization Heatmap</h4>
            <Heatmap
              data={heatmap.cells}
              paramXLabel={heatmap.paramXLabel}
              paramYLabel={heatmap.paramYLabel}
              valueLabel={heatmap.valueLabel}
            />
          </div>
        )}

        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            Trades ({result.trades.length})
          </h4>
          <ClientTable
            columns={tradeColumns}
            data={result.trades}
            pageSize={25}
            emptyMessage="No trades for this backtest."
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function OptimizationResults({ result }: { result: unknown }) {
  const parsed = optimizationResultSchema.safeParse(result);
  const data = parsed.success ? parsed.data : undefined;
  if (!data || !data.results || data.results.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Optimization Results</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState message="No optimization results. Change parameters and try again." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Optimization Results — {data.strategy ?? "unknown"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Stat
            label="Stability Score"
            value={
              data.stabilityScore !== undefined
                ? data.stabilityScore.toFixed(1)
                : "—"
            }
          />
          <Stat
            label="Best Params"
            value={JSON.stringify(data.bestParams ?? {})}
          />
          <Stat label="Total Runs" value={String(data.results.length)} />
        </div>

        {data.stabilityNotes && data.stabilityNotes.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-sm font-medium">Stability Notes</h4>
            <ul className="list-disc pl-5 text-sm text-muted-foreground">
              {data.stabilityNotes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          </div>
        )}

        {data.heatmap && data.heatmap.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Heatmap</h4>
            <Heatmap
              data={data.heatmap.map((p) => ({
                paramX: String(p.valueX),
                paramY: String(p.valueY),
                value: p.totalReturn,
              }))}
              paramXLabel={data.heatmap[0]?.paramX ?? "X"}
              paramYLabel={data.heatmap[0]?.paramY ?? "Y"}
              valueLabel="Total Return"
            />
          </div>
        )}

        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            Parameter Runs ({data.results.length})
          </h4>
          <div className="overflow-auto">
            <SmartTable
              columns={optimizationRunColumns}
              data={data.results}
              total={data.results.length}
              page={1}
              pageSize={50}
              isLoading={false}
              error={null}
              filters={[]}
              sort={[]}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              onFiltersChange={() => {}}
              onSortChange={() => {}}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
